// src/views/admin/ADPointage.jsx
//
// PERMISSIONS (module "pointage") :
// - pointage.voir                    : accès à la page (déjà géré sidebar / route)
// - pointage.saisir                  : onglet "Pointage manuel" (cartes Entrée/Sortie,
//                                      "Ajouter un pointage") + "Enregistrer présent"
//                                      dans l'onglet "Non pointés"
// - pointage.modifier                : bouton "Modifier" d'un pointage (onglet Pointage manuel)
// - pointage.notifier_absences       : bloc "Notifier les absences du jour"
// - pointage.exporter                : bouton "Exporter PDF"
// - pointage.traiter_justifications  : boutons Approuver / Rejeter (onglet Justifications)
//
// L'onglet "Pointage manuel" est visible avec pointage.saisir OU pointage.modifier.
// Les autres onglets sont visibles pour qui a accès à la page.

import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { usePermissions } from "../../context/PermissionsContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function Modal({ title, onClose, children, maxWidth = 450 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const STATUT_PRESENCE_LABELS = {
  PRESENT: { label: "Présent", cls: "ok" },
  RETARD: { label: "Retard", cls: "warn" },
  ABSENT: { label: "Absent", cls: "rd" },
};

const PERIODE_LABELS = {
  matin: "Matin",
  apres_midi: "Après-midi",
  pause: "Pause",
  descente: "Descente",
  autre: "Exceptionnelle",
};

// Couleur + icône par période, repris tel quel de l'ancien design : texte en
// gras coloré avec icône (pas de pastille de fond) — matin=bleu, après-midi=
// vert, pause=cyan, descente=rouge, exceptionnelle=orange.
const PERIODE_META = {
  matin: { color: "#0d6efd", icon: "ti-sunrise" },
  apres_midi: { color: "#198754", icon: "ti-sunset" },
  pause: { color: "#0dcaf0", icon: "ti-coffee" },
  descente: { color: "#dc3545", icon: "ti-arrow-down-circle" },
  autre: { color: "#ffc107", icon: "ti-alert-triangle" },
};

function PeriodePill({ periode }) {
  const meta = PERIODE_META[periode] || { color: "var(--m)", icon: "ti-clock" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700, color: meta.color }}>
      <i className={`ti ${meta.icon}`} />{PERIODE_LABELS[periode] || periode || "—"}
    </span>
  );
}

const STATUT_JUSTIF_LABELS = {
  en_attente: { label: "En attente", cls: "neu" },
  approuvee: { label: "Approuvée", cls: "ok" },
  rejetee: { label: "Rejetée", cls: "rd" },
};

const MOTIF_JUSTIF_LABELS = {
  maladie: "Maladie",
  famille: "Événement familial",
  transport: "Transport",
  autre: "Autre",
};

function formatDateTime(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("fr-FR"); } catch { return d; }
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function nowHHMM() {
  return new Date().toTimeString().slice(0, 5);
}
function nowHHMMSS() {
  return new Date().toTimeString().slice(0, 8);
}
// Normalise une heure "HH:MM", "HH:MM:SS" ou "HH:MM:SS.ffffff" en "HH:MM:SS".
function formatHeure(heure) {
  if (!heure) return "—";
  const sansMicro = heure.split(".")[0]; // retire les microsecondes éventuelles
  const parts = sansMicro.split(":");
  if (parts.length >= 3) return `${parts[0]}:${parts[1]}:${parts[2]}`;
  if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
  return sansMicro;
}
function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
}

// ─── Règles horaires (paramètres pointage) ───────────────────────────────
// Reprend la logique de l'ancienne page pointage manuel : période auto-
// détectée selon l'heure, motif exigé uniquement pour une sortie anticipée
// (hors pause, avant l'heure de sortie minimale).
function determinerPeriode(heureStr, parametres) {
  if (!parametres) return "matin";
  const current = timeToMinutes(heureStr);
  if (parametres.heure_autorisee != null) {
    const seuil = timeToMinutes(parametres.heure_autorisee);
    if (current != null && seuil != null) return current >= seuil ? "apres_midi" : "matin";
  }
  return "matin";
}

function motifRequisPourSortie(heureStr, parametres) {
  if (!parametres) return true; // par sécurité, tant que les paramètres ne sont pas chargés
  const current = timeToMinutes(heureStr);
  if (current == null) return true;
  const pauseDebut = timeToMinutes(parametres.pause_debut);
  const pauseFin = timeToMinutes(parametres.pause_fin);
  const heureSortieMin = timeToMinutes(parametres.heure_sortie_min);
  if (pauseDebut != null && pauseFin != null && current >= pauseDebut && current <= pauseFin) return false;
  if (heureSortieMin != null && current >= heureSortieMin) return false;
  return true;
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────
export default function ADPointage({ slug }) {
  const { canSee } = usePermissions();
  // L'onglet "Pointage manuel" sert à saisir ET à modifier des pointages.
  const voirOngletManuel = canSee("pointage.saisir") || canSee("pointage.modifier");

  const [activeTab, setActiveTab] = useState("jour");
  const [classes, setClasses] = useState([]);
  const [classeFilt, setClasseFilt] = useState("");
  const [loadingClasses, setLoadingClasses] = useState(true);

  const toast = useToastHook();
  const { getClasses } = useAdminService();

  useEffect(() => { if (slug) initClasses(); }, [slug]);

  const initClasses = async () => {
    setLoadingClasses(true);
    try {
      const data = await getClasses(slug, { show_archived: "false" });
      setClasses(data);
      if (data.length) setClasseFilt(String(data[0].id));
    } catch (error) {
      toast.error("Impossible de charger les classes");
    } finally {
      setLoadingClasses(false);
    }
  };

  if (loadingClasses) return <Loader />;

  return (
    <div>
      {/* Onglets comme dans Configuration */}
      <div className="tab-nav">
        <button
          className={`tab-btn ${activeTab === "jour" ? "active" : ""}`}
          onClick={() => setActiveTab("jour")}
        >
          <i className="ti ti-calendar-check" /> PRÉSENCES DU JOUR
        </button>
        <button
          className={`tab-btn ${activeTab === "historique" ? "active" : ""}`}
          onClick={() => setActiveTab("historique")}
        >
          <i className="ti ti-history" /> HISTORIQUE
        </button>
        <button
          className={`tab-btn ${activeTab === "nonpointes" ? "active" : ""}`}
          onClick={() => setActiveTab("nonpointes")}
        >
          <i className="ti ti-user-exclamation" /> NON POINTÉS
        </button>
        {voirOngletManuel && (
          <button
            className={`tab-btn ${activeTab === "manuel" ? "active" : ""}`}
            onClick={() => setActiveTab("manuel")}
          >
            <i className="ti ti-hand-click" /> POINTAGE MANUEL
          </button>
        )}
        <button
          className={`tab-btn ${activeTab === "justifications" ? "active" : ""}`}
          onClick={() => setActiveTab("justifications")}
        >
          <i className="ti ti-file-check" /> JUSTIFICATIONS
        </button>
      </div>


      {/* Sélecteur de classe — repris de l'ancien design : pastilles cliquables
          plutôt qu'un simple select, pour une identification visuelle rapide. */}
      {activeTab !== "justifications" && (
        <div className="classe-selector">
          {classes.map(c => (
            <button
              key={c.id}
              className={`classe-btn ${String(classeFilt) === String(c.id) ? "active" : ""}`}
              onClick={() => setClasseFilt(String(c.id))}
            >
              <i className="ti ti-school" />{c.nom}
            </button>
          ))}
          {classes.length === 0 && (
            <div style={{ fontSize: 13, color: "var(--h)" }}>Aucune classe disponible.</div>
          )}
        </div>
      )}

      {activeTab !== "justifications" && !classeFilt && (
        <div style={{ fontSize: 13, color: "var(--h)" }}>Choisis une classe pour continuer.</div>
      )}

      {activeTab === "jour" && classeFilt && (
        <PresencesJourPanel
          slug={slug}
          classeId={classeFilt}
          classeNom={classes.find(c => String(c.id) === String(classeFilt))?.nom}
        />
      )}
      {activeTab === "historique" && classeFilt && <HistoriquePanel slug={slug} classeId={classeFilt} />}
      {activeTab === "nonpointes" && classeFilt && <NonPointesPanel slug={slug} classeId={classeFilt} />}
      {activeTab === "manuel" && voirOngletManuel && classeFilt && (
        <PointageManuelPanel slug={slug} classeId={classeFilt} />
      )}
      {activeTab === "justifications" && <JustificationsPanel slug={slug} classes={classes} />}

      <style>{`
/* ─── ONGLETS (thème bleu + or) ─── */
.tab-nav {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
  border-radius: 10px;
  border-bottom: 3px solid var(--or);
  padding: 8px 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  color: rgba(255,255,255,0.75);
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.tab-btn i { font-size: 18px; }

.tab-btn:hover {
  background: rgba(255,255,255,0.12);
  color: var(--or-lt);
}

.tab-btn.active {
  background: white;
  color: var(--pr);
  box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
}
.tab-btn.active i { color: var(--or); }
.tab-btn.active:hover { background: white; color: var(--pr); }

        /* ─── SÉLECTEUR DE CLASSE (pastilles) ─── */
        .classe-selector {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .classe-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border-radius: 10px;
          border: 0.5px solid var(--b2);
          background: var(--s);
          color: var(--t);
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          font-family: inherit;
          transition: all .15s ease;
          min-width: 120px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
        }
        .classe-btn i { font-size: 16px; }
        .classe-btn:hover { background: var(--s2); }
        .classe-btn.active {
          border-color: var(--pr);
          background: var(--pr);
          color: #fff;
        }
        .classe-btn.active:hover { background: var(--pr); }

        .pill {
          display: inline-block;
          padding: 4px 9px;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 500;
        }
        .pill.ok  { background: var(--gn-bg); color: var(--gn); }
        .pill.warn{ background: var(--am-bg); color: var(--am); }
        .pill.err { background: var(--rd-bg); color: var(--rd); }
        .pill.info{ background: var(--bl-bg); color: var(--bl-dk); }
        .pill.pu  { background: var(--pr-bg); color: var(--pr); }
        .pill.neu { background: var(--s2); color: var(--m); }
        .pill.rd  { background: var(--rd-bg); color: var(--rd); }

        .card {
          background: var(--s);
          border: 0.5px solid var(--b);
          border-radius: var(--radius-md);
          padding: 14px;
        }

        .avatar-sm {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          object-fit: cover;
          cursor: pointer;
          box-shadow: 0 1px 4px rgba(0,0,0,0.15);
          transition: transform .12s;
        }
        .avatar-sm:hover { transform: scale(1.12); }
        .avatar-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--s2);
          color: var(--m);
          font-size: 16px;
          cursor: default;
        }
        .avatar-placeholder:hover { transform: none; }

        .bprim {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 15px;
          background: var(--pr);
          color: #fff;
          border: none;
          border-radius: 5px;
          font-size: 15px;
          cursor: pointer;
          font-family: inherit;
          font-weight: 500;
          white-space: nowrap;
          transition: opacity .12s;
        }
        .bprim:hover { opacity: .88; }
        .bprim.rd { background: var(--rd); }

        .bsec {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 13px;
          background: var(--s);
          color: var(--m);
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          cursor: pointer;
          font-family: inherit;
          transition: all .12s;
        }
        .bsec:hover { background: var(--s2); color: var(--t); }

        .sb2 {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 11px;
          flex-wrap: wrap;
        }
        .sb2 input {
          flex: 1;
          min-width: 120px;
          padding: 8px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
        }
        .sb2 select {
          padding: 8px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
        }

        .factions {
          display: flex;
          gap: 7px;
          margin-top: 12px;
          justify-content: flex-end;
        }

        .ibtn {
          width: 30px;
          height: 30px;
          border-radius: 5px;
          border: 0.5px solid var(--b);
          background: var(--s);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--m);
          font-size: 17px;
          transition: all .12s;
        }
        .ibtn:hover { background: var(--s2); color: var(--t); }

        /* ─── TABLEAUX ───
           table-layout: fixed + largeurs explicites sur le thead pour que
           chaque colonne garde une part d'espace garantie. Les colonnes à
           texte variable (Élève, Classe, Motif) passent à la ligne
           (white-space: normal) au lieu d'être tronquées ; les colonnes
           courtes (Heure, Type, Statut, Justifié, Actions...) restent sur
           une seule ligne via la classe .nowrap. */
        .tbl {
          width: 100%;
          border-collapse: collapse;
          font-size: 15px;
          table-layout: fixed;
        }
        .tbl th {
          text-align: left;
          padding: 9px 10px;
          color: var(--m);
          font-weight: 600;
          border-bottom: 0.5px solid var(--b);
          font-size: 14px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .tbl td {
          padding: 10px 10px;
          border-bottom: 0.5px solid var(--b);
          color: var(--t);
          white-space: normal;
          word-break: break-word;
          vertical-align: top;
        }
        .tbl td.nowrap {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* Heure (HH:MM:SS) : format fixe et court, jamais tronqué, sans
           déborder sur les colonnes voisines. */
        .tbl td.heure-cell {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: clip;
          font-variant-numeric: tabular-nums;
        }
        .tbl tr:last-child td { border-bottom: none; }
        .tbl tr:hover td { background: var(--s2); }

        .act {
          display: flex;
          gap: 3px;
        }
        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
        }
        .act button:hover {
          opacity: 0.8 !important;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.35);
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }
        .modal-box {
          background: var(--s);
          border-radius: var(--radius-lg);
          border: 0.5px solid var(--b);
          padding: 18px;
          max-width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-title {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin-bottom: 13px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .fg {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .fg label {
          font-size: 14px;
          color: var(--m);
          font-weight: 500;
        }
        .fg input, .fg select, .fg textarea {
          padding: 9px 13px;
          border: 0.5px solid var(--b2);
          border-radius: 5px;
          font-size: 15px;
          background: var(--s);
          color: var(--t);
          font-family: inherit;
          width: 100%;
        }
        .fg textarea {
          resize: vertical;
          min-height: 56px;
        }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PRÉSENCES DU JOUR
// ═══════════════════════════════════════════════════════════════════════
function PresencesJourPanel({ slug, classeId, classeNom }) {
  const { canSee } = usePermissions();
  const peutNotifier = canSee("pointage.notifier_absences");
  const peutExporter = canSee("pointage.exporter");

  const [date, setDate] = useState(todayStr());
  const [search, setSearch] = useState("");
  const [periode, setPeriode] = useState("");
  const [statut, setStatut] = useState("");
  // Filtre "nombre de lignes" repris de l'ancienne page (10/20/30/50/tout)
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ count: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Confirmation avant envoi des notifications — reprend le double-check de
  // l'ancienne page (SweetAlert "Voulez-vous envoyer les notifications ?").
  const [confirmNotif, setConfirmNotif] = useState(null); // { toutes: bool } | null

  const toast = useToastHook();
  const { getPresencesClasse, notifierAbsences } = useAdminService();

  useEffect(() => { loadPage(1); }, [slug, classeId, date, search, periode, statut, pageSize]);

  const loadPage = async (targetPage = page) => {
    setLoading(true);
    try {
      const res = await getPresencesClasse(slug, classeId, {
        date, search, periode, statut,
        page: targetPage, page_size: pageSize === "all" ? "all" : pageSize,
      });
      setData(res);
      setPage(targetPage);
    } catch (error) {
      toast.error("Impossible de charger les présences");
    } finally {
      setLoading(false);
    }
  };

  const handleNotifierAbsences = async (toutes = false) => {
    setNotifying(true);
    try {
      const res = await notifierAbsences(slug, { classe_id: toutes ? null : classeId });
      toast.success(res.message || `${res.envoyees ?? 0} notification(s) envoyée(s)`);
    } catch (error) {
      toast.error("Impossible d'envoyer les notifications");
    } finally {
      setNotifying(false);
    }
  };

  // L'export PDF récupère toujours l'intégralité des résultats filtrés
  // (indépendamment de la pagination affichée à l'écran), comme dans l'ancien code.
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const full = await getPresencesClasse(slug, classeId, {
        date, search, periode, statut, page_size: "all",
      });
      const rows = full.results || [];
      if (!rows.length) { toast.error("Aucune présence à exporter"); return; }
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(`Présences du ${date}${classeNom ? " — " + classeNom : ""}`, 14, 18);
      autoTable(doc, {
        startY: 26,
        head: [["Élève", "Heure", "Type", "Période", "Statut", "Motif"]],
        body: rows.map(p => [
          `${p.eleve_prenom} ${p.eleve_nom}`,
          formatHeure(p.heure),
          p.type_pointage === "entree" ? "Entrée" : p.type_pointage === "sortie" ? "Sortie" : "—",
          PERIODE_LABELS[p.periode] || p.periode || "—",
          p.statut || "—",
          p.motif_sortie || "—",
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [0, 64, 128] },
      });
      doc.save(`presences_${date}.pdf`);
    } catch (error) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = pageSize === "all" ? 1 : Math.ceil((data.count || 0) / pageSize) || 1;
  const pageSizeOptions = () => {
    const opts = [10, 20, 30, 50];
    if (data.count > 50 && !opts.includes(data.count)) opts.push(data.count);
    opts.push("all");
    return opts;
  };
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    loadPage(newPage);
  };

  return (
    <div>
      {/* pointage.notifier_absences */}
      {peutNotifier && (
        <div className="card" style={{ marginBottom: 12, borderLeft: "3px solid var(--rd)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, color: "var(--rd)" }}>
              <i className="ti ti-alert-triangle" />Notifier les absences du jour
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="bprim rd" onClick={() => setConfirmNotif({ toutes: false })} disabled={notifying}>
                <i className="ti ti-bell-ringing" />{notifying ? "Envoi..." : "Notifier cette classe"}
              </button>
              <button className="bprim" style={{ background: "#ff6b6b" }} onClick={() => setConfirmNotif({ toutes: true })} disabled={notifying}>
                <i className="ti ti-bell-ringing" />Notifier toutes les classes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sb2" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        <input type="text" placeholder="Rechercher un élève…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={periode} onChange={e => setPeriode(e.target.value)}>
          <option value="">Toutes les périodes</option>
          {Object.entries(PERIODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={statut} onChange={e => setStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_PRESENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={pageSize} onChange={e => setPageSize(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}>
          {pageSizeOptions().map((size, idx) => (
            <option key={idx} value={size}>{size === "all" ? "Tous afficher" : `${size} lignes`}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        {/* pointage.exporter */}
        {peutExporter && (
          <button className="bsec" onClick={handleExportPDF} disabled={exporting || !data.count}>
            <i className="ti ti-download" />{exporting ? "..." : "Exporter PDF"}
          </button>
        )}
        <button className="bsec" onClick={() => loadPage(page)}><i className="ti ti-refresh" />Actualiser</button>
      </div>

      {loading ? <Loader /> : (
        <>
          <PresenceTable
            rows={data.results || []}
            count={data.count}
            showActions={false}
          />

          {pageSize !== "all" && totalPages > 1 && (
            <div className="sb2" style={{ justifyContent: "center", marginTop: 8 }}>
              <button className="bsec" onClick={() => handlePageChange(1)} disabled={page === 1}>Première</button>
              <button className="bsec" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>&laquo;</button>
              <span style={{ fontSize: 13, color: "var(--m)", padding: "0 8px" }}>Page {page} sur {totalPages}</span>
              <button className="bsec" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>&raquo;</button>
              <button className="bsec" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>Dernière</button>
            </div>
          )}
        </>
      )}

      {peutNotifier && confirmNotif && (
        <Modal title="Confirmer l'envoi" onClose={() => setConfirmNotif(null)}>
          <div style={{ fontSize: 15, color: "var(--t)", marginBottom: 12 }}>
            Voulez-vous envoyer les notifications d'absence ?
          </div>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            <strong style={{ color: "var(--t)" }}>Classe :</strong>{" "}
            {confirmNotif.toutes ? "Toutes les classes" : (classeNom || "Classe sélectionnée")}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setConfirmNotif(null)} disabled={notifying}>Annuler</button>
            <button
              className="bprim rd"
              disabled={notifying}
              onClick={() => { const toutes = confirmNotif.toutes; setConfirmNotif(null); handleNotifierAbsences(toutes); }}
            >
              {notifying ? "Envoi..." : "Oui, envoyer"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// HISTORIQUE
// ═══════════════════════════════════════════════════════════════════════
function HistoriquePanel({ slug, classeId }) {
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [search, setSearch] = useState("");
  const [periode, setPeriode] = useState("");
  const [statut, setStatut] = useState("");
  const [typePointage, setTypePointage] = useState("");
  // Filtre "nombre de lignes" repris de l'ancienne page (10/20/30/50/tout)
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ count: 0, results: [] });
  const [loading, setLoading] = useState(true);

  const toast = useToastHook();
  const { getPresencesHistorique } = useAdminService();

  useEffect(() => { loadPage(1); }, [slug, classeId, month, search, periode, statut, typePointage, pageSize]);

  const loadPage = async (targetPage = page) => {
    setLoading(true);
    try {
      const res = await getPresencesHistorique(slug, classeId, {
        month, search, periode, statut, type_pointage: typePointage,
        page: targetPage, page_size: pageSize === "all" ? "all" : pageSize,
      });
      setData(res);
      setPage(targetPage);
    } catch (error) {
      toast.error("Impossible de charger l'historique");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = pageSize === "all" ? 1 : Math.ceil((data.count || 0) / pageSize) || 1;
  const pageSizeOptions = () => {
    const opts = [10, 20, 30, 50];
    if (data.count > 50 && !opts.includes(data.count)) opts.push(data.count);
    opts.push("all");
    return opts;
  };
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    loadPage(newPage);
  };

  return (
    <div>
      <div className="sb2" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} />
        <input type="text" placeholder="Rechercher un élève…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={periode} onChange={e => setPeriode(e.target.value)}>
          <option value="">Toutes les périodes</option>
          {Object.entries(PERIODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={statut} onChange={e => setStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_PRESENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={typePointage} onChange={e => setTypePointage(e.target.value)}>
          <option value="">Entrée/Sortie</option>
          <option value="entree">Entrée</option>
          <option value="sortie">Sortie</option>
        </select>
        <select value={pageSize} onChange={e => setPageSize(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}>
          {pageSizeOptions().map((size, idx) => (
            <option key={idx} value={size}>{size === "all" ? "Tous afficher" : `${size} lignes`}</option>
          ))}
        </select>
      </div>

      {loading ? <Loader /> : (
        <>
          <PresenceTable rows={data.results || []} count={data.count} showDate showActions={false} />

          {pageSize !== "all" && totalPages > 1 && (
            <div className="sb2" style={{ justifyContent: "center", marginTop: 8 }}>
              <button className="bsec" onClick={() => handlePageChange(1)} disabled={page === 1}>Première</button>
              <button className="bsec" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>&laquo;</button>
              <span style={{ fontSize: 13, color: "var(--m)", padding: "0 8px" }}>Page {page} sur {totalPages}</span>
              <button className="bsec" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>&raquo;</button>
              <button className="bsec" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>Dernière</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TABLE PARTAGÉE (jour / historique / manuel)
// ═══════════════════════════════════════════════════════════════════════
function PresenceTable({ rows, count, showDate = false, showActions = true, onEdit }) {
  const [motifDetail, setMotifDetail] = useState(null); // { nom, motif } | null
  const [photoPreview, setPhotoPreview] = useState(null); // { nom, src } | null
  const colSpan = 9 + (showDate ? 1 : 0) + (showActions ? 1 : 0);

  return (
    <div className="card" style={{ padding: 0, overflow: "auto" }}>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 46 }}>Photo</th>
            <th style={{ width: 170 }}>Élève</th>
            <th style={{ width: 100 }}>Classe</th>
            {showDate && <th style={{ width: 90 }}>Date</th>}
            <th style={{ width: 90 }}>Heure</th>
            <th style={{ width: 70 }}>Type</th>
            <th style={{ width: 130 }}>Période</th>
            <th style={{ width: 90 }}>Statut</th>
            <th style={{ width: 80 }}>Justifié</th>
            <th style={{ width: 160 }}>Motif sortie</th>
            {showActions && <th style={{ width: 60 }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(p => {
            const st = STATUT_PRESENCE_LABELS[p.statut] || null;
            const nomComplet = `${p.eleve_prenom} ${p.eleve_nom}`;
            return (
              <tr key={p.id}>
                <td className="nowrap">
                  {p.photo ? (
                    <img
                      src={p.photo}
                      alt={nomComplet}
                      className="avatar-sm"
                      onClick={() => setPhotoPreview({ nom: nomComplet, src: p.photo })}
                      onError={e => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="avatar-sm avatar-placeholder"><i className="ti ti-user" /></div>
                  )}
                </td>
                <td style={{ fontWeight: 500 }} title={nomComplet}>{nomComplet}</td>
                <td title={p.classe_nom}>{p.classe_nom}</td>
                {showDate && <td className="nowrap">{p.date}</td>}
                <td className="heure-cell" title={formatHeure(p.heure)}>{formatHeure(p.heure)}</td>
                <td className="nowrap">{p.type_pointage === "entree" ? "Entrée" : p.type_pointage === "sortie" ? "Sortie" : "—"}</td>
                <td className="nowrap" style={{ overflow: "visible" }}><PeriodePill periode={p.periode} /></td>
                <td className="nowrap">{st ? <span className={`pill ${st.cls}`}>{st.label}</span> : "—"}</td>
                <td className="nowrap">{p.statut === "ABSENT" ? (p.est_justifie ? <span className="pill ok">Oui</span> : <span className="pill rd">Non</span>) : "—"}</td>
                <td style={{ fontSize: 13 }}>
                  {p.motif_sortie ? (
                    <button
                      className="bsec"
                      style={{ padding: "4px 9px", fontSize: 13 }}
                      onClick={() => setMotifDetail({ nom: nomComplet, motif: p.motif_sortie })}
                    >
                      <i className="ti ti-info-circle" />Détail
                    </button>
                  ) : "—"}
                </td>
                {showActions && (
                  <td className="nowrap">
                    <div className="act">
                      <button
                        title="Modifier"
                        onClick={() => onEdit(p)}
                        style={{ background: "#ffc107", color: "black" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={colSpan} style={{ textAlign: "center", color: "var(--h)", padding: 24 }}>
              Aucune présence trouvée
            </td></tr>
          )}
        </tbody>
      </table>
      <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--m)" }}>{count} résultat(s)</div>

      {motifDetail && (
        <Modal title={`Motif de sortie — ${motifDetail.nom}`} onClose={() => setMotifDetail(null)}>
          <div style={{ fontSize: 15, color: "var(--t)", whiteSpace: "pre-wrap" }}>
            {motifDetail.motif}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setMotifDetail(null)}>Fermer</button>
          </div>
        </Modal>
      )}

      {photoPreview && (
        <Modal title={photoPreview.nom} onClose={() => setPhotoPreview(null)} maxWidth={380}>
          <img src={photoPreview.src} alt={photoPreview.nom} style={{ width: "100%", borderRadius: 10 }} />
        </Modal>
      )}
    </div>
  );
}

function PresenceEditModal({ presence, working, onClose, onSave }) {
  const [heure, setHeure] = useState(presence.heure || "");
  const [typePointage, setTypePointage] = useState(presence.type_pointage || "entree");
  const [periode, setPeriode] = useState(presence.periode || "matin");
  const [motif, setMotif] = useState(presence.motif_sortie || "");

  return (
    <Modal title={`Modifier — ${presence.eleve_prenom} ${presence.eleve_nom}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Heure</label>
          <input type="time" step="1" value={heure} onChange={e => setHeure(e.target.value)} />
        </div>
        <div className="fg">
          <label>Type de pointage</label>
          <select value={typePointage} onChange={e => setTypePointage(e.target.value)}>
            <option value="entree">Entrée</option>
            <option value="sortie">Sortie</option>
          </select>
        </div>
        <div className="fg">
          <label>Période</label>
          <select value={periode} onChange={e => setPeriode(e.target.value)}>
            {Object.entries(PERIODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {typePointage === "sortie" && periode === "autre" && (
          <div className="fg">
            <label>Motif de sortie</label>
            <input type="text" value={motif} onChange={e => setMotif(e.target.value)} />
          </div>
        )}
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={working}>Annuler</button>
        <button
          className="bprim"
          disabled={working}
          onClick={() => onSave({ heure, type_pointage: typePointage, periode, motif_sortie: motif || null })}
        >
          {working ? "..." : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ÉLÈVES NON POINTÉS + ENREGISTREMENT RAPIDE
// ═══════════════════════════════════════════════════════════════════════
function NonPointesPanel({ slug, classeId }) {
  const { canSee } = usePermissions();
  const peutSaisir = canSee("pointage.saisir");

  const [date, setDate] = useState(todayStr());
  const [periode, setPeriode] = useState("matin");
  const [eleves, setEleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [heureDefaut, setHeureDefaut] = useState(() => nowHHMMSS());
  const [working, setWorking] = useState(false);

  const toast = useToastHook();
  const { getElevesNonPointes, enregistrerPresencesRapide } = useAdminService();

  // Réinitialise la sélection uniquement quand les filtres changent (pas après
  // un enregistrement, où l'on veut garder les échecs cochés).
  useEffect(() => { setSelectedIds([]); load(); }, [slug, classeId, date, periode]);

  // load() ne touche plus à selectedIds : c'est aux appelants de décider quoi
  // faire de la sélection après un rechargement. Retourne la liste fraîche
  // pour que handleEnregistrer puisse s'en servir directement.
  const load = async () => {
    setLoading(true);
    try {
      const res = await getElevesNonPointes(slug, classeId, { date, periode });
      const nouveauxEleves = res.eleves || [];
      setEleves(nouveauxEleves);
      return nouveauxEleves;
    } catch (error) {
      toast.error("Impossible de charger les élèves non pointés");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedIds(eleves.map(e => e.id));
  const deselectAll = () => setSelectedIds([]);

  const handleEnregistrer = async () => {
    if (!peutSaisir) return;
    if (selectedIds.length === 0) { toast.error("Sélectionne au moins un élève"); return; }
    setWorking(true);
    try {
      const presencesEnvoyees = eleves
        .filter(e => selectedIds.includes(e.id))
        .map(e => ({ matricule: e.matricule, heure: heureDefaut }));

      const res = await enregistrerPresencesRapide(slug, {
        presences: presencesEnvoyees, classe_id: classeId, date, periode,
      });

      if (res.nombre_presences > 0) {
        toast.success(`${res.nombre_presences} présence(s) enregistrée(s)`);
      }

      // Rechargement de la liste des non-pointés (les réussis en sortent).
      const nouveauxEleves = await load();

      if (res.erreurs && res.erreurs.length > 0) {
        // Un toast par élève en échec, avec son nom plutôt que son matricule brut.
        res.erreurs.forEach(err => {
          const eleveEchec = eleves.find(e => e.matricule === err.matricule);
          const nom = eleveEchec ? `${eleveEchec.prenom} ${eleveEchec.nom}` : err.matricule;
          toast.error(`${nom} : ${err.message}`);
        });

        // Ne garde cochés que les élèves en échec, pour un nouvel essai rapide
        // sans tout recocher — à condition qu'ils soient toujours dans la
        // liste des non-pointés après rechargement.
        const matriculesEchecs = new Set(res.erreurs.map(e => e.matricule));
        const idsEchecs = nouveauxEleves
          .filter(e => matriculesEchecs.has(e.matricule))
          .map(e => e.id);
        setSelectedIds(idsEchecs);
      } else {
        setSelectedIds([]);
      }
    } catch (error) {
      toast.error("Impossible d'enregistrer les présences");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <div className="sb2" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        <select value={periode} onChange={e => setPeriode(e.target.value)}>
          <option value="matin">Matin</option>
          <option value="apres_midi">Après-midi</option>
        </select>
        {/* pointage.saisir : heure à appliquer + actions d'enregistrement */}
        {peutSaisir && (
          <input type="time" step="1" value={heureDefaut} onChange={e => setHeureDefaut(e.target.value)} title="Heure à appliquer" />
        )}
        <div style={{ flex: 1 }} />
        {peutSaisir && (
          <>
            <button className="bsec" onClick={selectAll}><i className="ti ti-checks" />Tout cocher</button>
            <button className="bsec" onClick={deselectAll}><i className="ti ti-x" />Tout décocher</button>
            <button className="bprim" onClick={handleEnregistrer} disabled={working || selectedIds.length === 0}>
              <i className="ti ti-check" />Enregistrer présent ({selectedIds.length})
            </button>
          </>
        )}
      </div>

      {loading ? <Loader /> : (
        <div className="card" style={{ padding: 0, overflow: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                {peutSaisir && <th style={{ width: 32 }}></th>}
                <th style={{ width: 220 }}>Élève</th>
                <th style={{ width: 140 }}>Matricule</th>
              </tr>
            </thead>
<tbody>
  {eleves.map(e => (
    <tr
      key={e.id}
      onClick={() => peutSaisir && toggle(e.id)}
style={{
  cursor: peutSaisir ? "pointer" : "default",
  background: selectedIds.includes(e.id) ? "var(--pr-bg, rgba(13,110,253,0.08))" : undefined,
}}
    >
      {peutSaisir && (
        <td className="nowrap" onClick={ev => ev.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedIds.includes(e.id)}
            onChange={() => toggle(e.id)}
          />
        </td>
      )}
      <td style={{ fontWeight: 500 }} title={`${e.prenom} ${e.nom}`}>{e.prenom} {e.nom}</td>
      <td className="nowrap">{e.matricule}</td>
    </tr>
  ))}
  {eleves.length === 0 && (
    <tr><td colSpan={peutSaisir ? 3 : 2} style={{ textAlign: "center", color: "var(--h)", padding: 24 }}>
      Tous les élèves ont pointé pour cette période
    </td></tr>
  )}
</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// POINTAGE MANUEL — cartes élèves + ajout complet (entrée/sortie/motif)
// ═══════════════════════════════════════════════════════════════════════
const CARDS_PER_PAGE = 6; // repris de l'ancienne page — pagination des cartes élèves

function PointageManuelPanel({ slug, classeId }) {
  const { canSee } = usePermissions();
  const peutSaisir = canSee("pointage.saisir");
  const peutModifier = canSee("pointage.modifier");

  const [date, setDate] = useState(todayStr());
  const [search, setSearch] = useState("");
  const [periodeFilter, setPeriodeFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [eleves, setEleves] = useState([]);
  const [loadingEleves, setLoadingEleves] = useState(true);
  const [cardsPage, setCardsPage] = useState(1);
  const [presences, setPresences] = useState({ count: 0, results: [] });
  const [loadingPresences, setLoadingPresences] = useState(true);
  const [ajoutInitial, setAjoutInitial] = useState(null); // { eleve, type_pointage } | null
  const [rapide, setRapide] = useState(null); // { eleve, type_pointage } | null — clic direct depuis la carte
  const [editing, setEditing] = useState(null);
  const [working, setWorking] = useState(false);
  const [parametres, setParametres] = useState(null);
  const [parametresLoaded, setParametresLoaded] = useState(false);

  const toast = useToastHook();
  const {
    getEleves, ajouterPresenceEleve, getPresencesClasse, modifierPresence,
    getParametres,
  } = useAdminService();

  useEffect(() => { loadEleves(); }, [slug, classeId]);
  useEffect(() => { loadPresences(1); }, [slug, classeId, date, search, periodeFilter, statutFilter, pageSize]);
  useEffect(() => { loadParametres(); }, [slug]);
  // Remet la pagination des cartes à 1 dès que la classe ou la recherche change
  useEffect(() => { setCardsPage(1); }, [classeId, search]);

  const loadParametres = async () => {
    try {
      const data = await getParametres(slug);
      setParametres(Array.isArray(data) ? data[0] : data);
    } catch (error) {
      // pas bloquant : le motif sera demandé par sécurité tant que rien n'est chargé
    } finally {
      setParametresLoaded(true);
    }
  };

  const loadEleves = async () => {
    setLoadingEleves(true);
    try {
      const data = await getEleves(slug, { classe_id: classeId });
      const results = data.results || data || [];
      // Sécurité : ne garder que les élèves ayant une inscription dans cette classe
      const elevesFiltres = results.filter(e =>
        !e.inscriptions || e.inscriptions.some(i => String(i.classe) === String(classeId))
      );
      setEleves(elevesFiltres);
    } catch (error) {
      toast.error("Impossible de charger les élèves de la classe");
    } finally {
      setLoadingEleves(false);
    }
  };

  const loadPresences = async (targetPage = page) => {
    setLoadingPresences(true);
    try {
      const res = await getPresencesClasse(slug, classeId, {
        date, search, periode: periodeFilter, statut: statutFilter,
        page: targetPage, page_size: pageSize === "all" ? "all" : pageSize,
      });
      setPresences(res);
      setPage(targetPage);
    } catch (error) {
      toast.error("Impossible de charger les présences");
    } finally {
      setLoadingPresences(false);
    }
  };

  const handleSaveAjout = async (payload) => {
    if (!peutSaisir) return;
    setWorking(true);
    try {
      await ajouterPresenceEleve(slug, payload);
      toast.success("Pointage enregistré");
      setAjoutInitial(null);
      await loadPresences(page);
    } catch (error) {
      const msg = error?.response?.data?.error || "Impossible d'enregistrer ce pointage";
      toast.error(msg);
    } finally {
      setWorking(false);
    }
  };

  const handleSaveRapide = async (payload) => {
    if (!peutSaisir) return;
    setWorking(true);
    try {
      await ajouterPresenceEleve(slug, payload);
      toast.success("Pointage enregistré");
      setRapide(null);
      await loadPresences(page);
    } catch (error) {
      const msg = error?.response?.data?.error || "Impossible d'enregistrer ce pointage";
      toast.error(msg);
    } finally {
      setWorking(false);
    }
  };

  const handleSaveEdit = async (patch) => {
    if (!peutModifier) return;
    setWorking(true);
    try {
      await modifierPresence(slug, editing.id, patch);
      toast.success("Présence modifiée");
      setEditing(null);
      await loadPresences(page);
    } catch (error) {
      toast.error("Impossible de modifier la présence");
    } finally {
      setWorking(false);
    }
  };

  const elevesFiltres = eleves.filter(e =>
    `${e.prenom} ${e.nom} ${e.matricule}`.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination des cartes élèves — reprise de l'ancienne page (6 par page)
  const totalCardsPages = Math.max(1, Math.ceil(elevesFiltres.length / CARDS_PER_PAGE));
  const cardsPageSafe = Math.min(cardsPage, totalCardsPages);
  const cardsToShow = elevesFiltres.slice((cardsPageSafe - 1) * CARDS_PER_PAGE, cardsPageSafe * CARDS_PER_PAGE);

  const totalPages = pageSize === "all" ? 1 : Math.ceil((presences.count || 0) / pageSize) || 1;
  const pageSizeOptions = () => {
    const opts = [10, 20, 30, 50];
    if (presences.count > 50 && !opts.includes(presences.count)) opts.push(presences.count);
    opts.push("all");
    return opts;
  };
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    loadPresences(newPage);
  };

  return (
    <div>
      <div className="sb2" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        <input
          type="text"
          placeholder="Rechercher un élève…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ flex: 1 }} />
        {/* pointage.saisir */}
        {peutSaisir && (
          <button className="bprim" onClick={() => setAjoutInitial({ eleve: null, type_pointage: "entree" })}>
            <i className="ti ti-plus" />Ajouter un pointage
          </button>
        )}
      </div>

      {/* Cartes élèves (boutons Entrée / Sortie) : uniquement avec pointage.saisir */}
      {peutSaisir && (
        loadingEleves ? <Loader /> : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 10,
                marginBottom: 10,
              }}
            >
              {cardsToShow.map(e => (
                <EleveCard
                  key={e.id}
                  eleve={e}
                  onEntree={() => setRapide({ eleve: e, type_pointage: "entree" })}
                  onSortie={() => setRapide({ eleve: e, type_pointage: "sortie" })}
                />
              ))}
              {elevesFiltres.length === 0 && (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "var(--h)", padding: 24 }}>
                  Aucun élève trouvé
                </div>
              )}
            </div>

            {totalCardsPages > 1 && (
              <div className="sb2" style={{ justifyContent: "center", marginBottom: 16 }}>
                <button className="bsec" onClick={() => setCardsPage(p => Math.max(1, p - 1))} disabled={cardsPageSafe === 1}>
                  Précédent
                </button>
                <span style={{ fontSize: 13, color: "var(--m)", padding: "0 8px" }}>
                  Page {cardsPageSafe} sur {totalCardsPages}
                </span>
                <button className="bsec" onClick={() => setCardsPage(p => Math.min(totalCardsPages, p + 1))} disabled={cardsPageSafe === totalCardsPages}>
                  Suivant
                </button>
              </div>
            )}
          </>
        )
      )}

      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--t)", margin: "4px 0 8px" }}>
        Présences enregistrées le {date}
      </div>

      <div className="sb2" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        <select value={periodeFilter} onChange={e => setPeriodeFilter(e.target.value)}>
          <option value="">Toutes les périodes</option>
          {Object.entries(PERIODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={statutFilter} onChange={e => setStatutFilter(e.target.value)}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_PRESENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={pageSize} onChange={e => setPageSize(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}>
          {pageSizeOptions().map((size, idx) => (
            <option key={idx} value={size}>{size === "all" ? "Tous afficher" : `${size} lignes`}</option>
          ))}
        </select>
      </div>

      {loadingPresences ? <Loader /> : (
        <>
          {/* pointage.modifier : colonne Actions */}
          <PresenceTable
            rows={presences.results || []}
            count={presences.count}
            showActions={peutModifier}
            onEdit={setEditing}
          />

          {pageSize !== "all" && totalPages > 1 && (
            <div className="sb2" style={{ justifyContent: "center", marginTop: 8 }}>
              <button className="bsec" onClick={() => handlePageChange(1)} disabled={page === 1}>Première</button>
              <button className="bsec" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>&laquo;</button>
              <span style={{ fontSize: 13, color: "var(--m)", padding: "0 8px" }}>Page {page} sur {totalPages}</span>
              <button className="bsec" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>&raquo;</button>
              <button className="bsec" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>Dernière</button>
            </div>
          )}
        </>
      )}

      {peutSaisir && ajoutInitial && (
        <AjoutPointageModal
          eleves={eleves}
          initial={ajoutInitial}
          date={date}
          working={working}
          onClose={() => setAjoutInitial(null)}
          onSave={handleSaveAjout}
        />
      )}

      {peutSaisir && rapide && (
        <RapidePointageModal
          eleve={rapide.eleve}
          typePointage={rapide.type_pointage}
          date={date}
          parametres={parametres}
          parametresLoaded={parametresLoaded}
          working={working}
          onClose={() => setRapide(null)}
          onSave={handleSaveRapide}
        />
      )}

      {peutModifier && editing && (
        <PresenceEditModal
          presence={editing}
          working={working}
          onClose={() => setEditing(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}

// Carte élève enrichie — reprend genre + date de naissance de l'ancienne page,
// en plus des boutons rapides Entrée/Sortie.
function EleveCard({ eleve, onEntree, onSortie }) {
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div>
        <div style={{ fontWeight: 600, color: "var(--t)" }}>{eleve.prenom} {eleve.nom}</div>
        <div style={{ fontSize: 12, color: "var(--m)" }}>{eleve.matricule}</div>
        {eleve.genre && (
          <div style={{ fontSize: 12, color: "var(--m)" }}>
            Genre : {eleve.genre === "M" ? "Masculin" : "Féminin"}
          </div>
        )}
        {eleve.date_naissance && (
          <div style={{ fontSize: 12, color: "var(--m)" }}>
            Né(e) le {new Date(eleve.date_naissance).toLocaleDateString("fr-FR")}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="bprim" style={{ flex: 1, justifyContent: "center", background: "var(--gn, #28a745)" }} onClick={onEntree}>
          <i className="ti ti-login" />Entrée
        </button>
        <button className="bprim rd" style={{ flex: 1, justifyContent: "center" }} onClick={onSortie}>
          <i className="ti ti-logout" />Sortie
        </button>
      </div>
    </div>
  );
}

// Flux rapide déclenché depuis les boutons Entrée/Sortie de la carte élève :
// période auto-détectée, motif demandé seulement si la sortie est anticipée,
// puis un écran de confirmation avant l'enregistrement définitif — comme
// dans l'ancienne page pointage manuel (motif → confirmation → envoi).
function RapidePointageModal({ eleve, typePointage, date, parametres, parametresLoaded, working, onClose, onSave }) {
  const [step, setStep] = useState("form"); // "form" | "confirm"
  const [heure, setHeure] = useState(nowHHMMSS());
  const [motif, setMotif] = useState("");

  const periode = determinerPeriode(heure, parametres);
  const motifRequis = typePointage === "sortie" && motifRequisPourSortie(heure, parametres);

  const handleContinuer = () => {
    if (motifRequis && !motif.trim()) return;
    setStep("confirm");
  };

  const handleConfirmer = () => {
    onSave({
      matricule: eleve.matricule,
      type_pointage: typePointage,
      periode,
      date,
      heure,
      motif_sortie: motifRequis ? motif : undefined,
    });
  };

  if (step === "confirm") {
    return (
      <Modal title="Confirmer le pointage" onClose={onClose}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <div style={{ fontSize: 15, color: "var(--t)" }}>
            Êtes-vous sûr de vouloir enregistrer une {typePointage === "entree" ? "entrée" : "sortie"} pour :
          </div>
          <div style={{ fontWeight: 600, color: "var(--t)" }}>{eleve.prenom} {eleve.nom}</div>
          <div style={{ fontSize: 13, color: "var(--m)" }}>Matricule : {eleve.matricule}</div>
          <div style={{ fontSize: 13, color: "var(--m)" }}>
            Heure : {formatHeure(heure)} — <span className="pill info">{PERIODE_LABELS[periode]}</span>
          </div>
          {motifRequis && motif && (
            <div className="card" style={{ background: "var(--s2)" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 4 }}>Motif de sortie :</div>
              <div style={{ fontSize: 13, color: "var(--m)", fontStyle: "italic" }}>{motif}</div>
            </div>
          )}
        </div>
        <div className="factions">
          <button className="bsec" onClick={() => setStep("form")} disabled={working}>Retour</button>
          <button className="bprim" disabled={working} onClick={handleConfirmer}>
            {working ? "..." : "Oui, confirmer"}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={typePointage === "entree" ? "Confirmer l'entrée" : "Confirmer la sortie"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div>
          <div style={{ fontWeight: 600, color: "var(--t)" }}>{eleve.prenom} {eleve.nom}</div>
          <div style={{ fontSize: 12, color: "var(--m)" }}>{eleve.matricule}</div>
        </div>

        {!parametresLoaded && (
          <div style={{ fontSize: 13, color: "var(--m)" }}>Chargement des paramètres…</div>
        )}

        <div className="fg">
          <label>Heure</label>
          <input type="time" step="1" value={heure} onChange={e => setHeure(e.target.value)} />
        </div>

        <div style={{ fontSize: 13, color: "var(--m)" }}>
          Période détectée : <span className="pill info">{PERIODE_LABELS[periode]}</span>
        </div>

        {motifRequis && (
          <div className="fg">
            <label>Motif de sortie anticipée</label>
            <textarea
              rows={3}
              value={motif}
              onChange={e => setMotif(e.target.value)}
              placeholder="Cette sortie est hors des horaires normaux — précise le motif…"
            />
          </div>
        )}
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={working}>Annuler</button>
        <button
          className="bprim"
          disabled={working || !parametresLoaded || (motifRequis && !motif.trim())}
          onClick={handleContinuer}
        >
          {working ? "..." : "Continuer"}
        </button>
      </div>
    </Modal>
  );
}

function AjoutPointageModal({ eleves, initial, date, working, onClose, onSave }) {
  const [eleveId, setEleveId] = useState(initial.eleve?.id ? String(initial.eleve.id) : "");
  const [typePointage, setTypePointage] = useState(initial.type_pointage || "entree");
  const [dateVal, setDateVal] = useState(date);
  const [heure, setHeure] = useState(nowHHMMSS());
  const [periode, setPeriode] = useState("matin");
  const [motif, setMotif] = useState("");

  const eleveSelectionne = eleves.find(e => String(e.id) === eleveId) || initial.eleve;
  const motifRequis = typePointage === "sortie" && periode === "autre";

  const handleSubmit = () => {
    if (!eleveSelectionne) return;
    onSave({
      matricule: eleveSelectionne.matricule,
      type_pointage: typePointage,
      periode,
      date: dateVal,
      heure,
      motif_sortie: motifRequis ? motif : undefined,
    });
  };

  return (
    <Modal title="Ajouter un pointage" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="fg">
          <label>Élève</label>
          <select value={eleveId} onChange={e => setEleveId(e.target.value)} disabled={!!initial.eleve}>
            <option value="">-- Choisir un élève --</option>
            {eleves.map(e => (
              <option key={e.id} value={e.id}>{e.prenom} {e.nom} ({e.matricule})</option>
            ))}
          </select>
        </div>
        <div className="fg">
          <label>Type de pointage</label>
          <select value={typePointage} onChange={e => setTypePointage(e.target.value)}>
            <option value="entree">Entrée</option>
            <option value="sortie">Sortie</option>
          </select>
        </div>
        <div className="fg">
          <label>Date</label>
          <input type="date" value={dateVal} onChange={e => setDateVal(e.target.value)} />
        </div>
        <div className="fg">
          <label>Heure</label>
          <input type="time" step="1" value={heure} onChange={e => setHeure(e.target.value)} />
        </div>
        <div className="fg">
          <label>Période</label>
          <select value={periode} onChange={e => setPeriode(e.target.value)}>
            {Object.entries(PERIODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {motifRequis && (
          <div className="fg">
            <label>Motif de sortie exceptionnelle</label>
            <textarea rows={3} value={motif} onChange={e => setMotif(e.target.value)} placeholder="Précise le motif…" />
          </div>
        )}
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={working}>Annuler</button>
        <button
          className="bprim"
          disabled={working || !eleveSelectionne || (motifRequis && !motif.trim())}
          onClick={handleSubmit}
        >
          {working ? "..." : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// JUSTIFICATIONS D'ABSENCE
// ═══════════════════════════════════════════════════════════════════════
function JustificationsPanel({ slug, classes }) {
  const { canSee } = usePermissions();
  const peutTraiter = canSee("pointage.traiter_justifications");

  const [classeFilt, setClasseFilt] = useState("");
  const [statutFilt, setStatutFilt] = useState("en_attente");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [modal, setModal] = useState(null);

  const toast = useToastHook();
  const { getJustifications, approuverJustification, rejeterJustification, getJustificationsStats } = useAdminService();

  useEffect(() => { load(); loadStats(); }, [slug, classeFilt, statutFilt, dateDebut, dateFin]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getJustifications(slug, {
        classe_id: classeFilt, statut: statutFilt, date_debut: dateDebut, date_fin: dateFin,
      });
      setItems(res.results || res);
    } catch (error) {
      toast.error("Impossible de charger les justifications");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await getJustificationsStats(slug);
      setStats(res);
    } catch (error) {
      // silencieux, non bloquant
    }
  };

  const handleApprouver = async (item) => {
    if (!peutTraiter) return;
    setWorking(true);
    try {
      await approuverJustification(slug, item.id);
      toast.success("Justification approuvée");
      await load(); await loadStats();
    } catch (error) {
      toast.error("Impossible d'approuver cette justification");
    } finally {
      setWorking(false);
    }
  };

  const handleRejeter = async (item, commentaire) => {
    if (!peutTraiter) return;
    setWorking(true);
    try {
      await rejeterJustification(slug, item.id, commentaire);
      toast.success("Justification rejetée");
      setModal(null);
      await load(); await loadStats();
    } catch (error) {
      toast.error("Impossible de rejeter cette justification");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      {stats && (
        <div className="sb2" style={{ marginBottom: 10, flexWrap: "wrap" }}>
          <span className="pill neu">En attente : {stats.total_en_attente}</span>
          <span className="pill ok">Approuvées : {stats.total_approuvees}</span>
          <span className="pill rd">Rejetées : {stats.total_rejetees}</span>
        </div>
      )}

      <div className="sb2" style={{ flexWrap: "wrap", marginBottom: 8 }}>
        <select value={classeFilt} onChange={e => setClasseFilt(e.target.value)}>
          <option value="">Toutes les classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <select value={statutFilt} onChange={e => setStatutFilt(e.target.value)}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_JUSTIF_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} title="Du" />
        <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} title="Au" />
      </div>

      {loading ? <Loader /> : (
        <div className="card" style={{ padding: 0, overflow: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 170 }}>Élève</th>
                <th style={{ width: 100 }}>Classe</th>
                <th style={{ width: 100 }}>Date absence</th>
                <th style={{ width: 100 }}>Période</th>
                <th style={{ width: 220 }}>Motif</th>
                <th style={{ width: 90 }}>Statut</th>
                <th style={{ width: 120 }}>Soumis par</th>
                <th style={{ width: 90 }}>{peutTraiter ? "Actions" : "Traitée le"}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(j => {
                const st = STATUT_JUSTIF_LABELS[j.statut] || STATUT_JUSTIF_LABELS.en_attente;
                return (
                  <tr key={j.id}>
                    <td style={{ fontWeight: 500 }} title={`${j.eleve_prenom} ${j.eleve_nom}`}>
                      {j.eleve_prenom} {j.eleve_nom}
                      <div style={{ fontSize: 12, color: "var(--m)" }}>{j.eleve_matricule}</div>
                    </td>
                    <td title={j.classe_nom}>{j.classe_nom}</td>
                    <td className="nowrap">{j.date_absence}</td>
                    <td className="nowrap">{j.periode === "journee" ? "Journée" : PERIODE_LABELS[j.periode] || j.periode}</td>
                    <td>
                      {MOTIF_JUSTIF_LABELS[j.motif] || j.motif}
                      {j.description && <div style={{ fontSize: 12, color: "var(--m)" }}>{j.description}</div>}
                      {j.piece_jointe && (
                        <a href={j.piece_jointe} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                          <i className="ti ti-paperclip" />Pièce jointe
                        </a>
                      )}
                    </td>
                    <td className="nowrap"><span className={`pill ${st.cls}`}>{st.label}</span></td>
                    <td style={{ fontSize: 13, color: "var(--m)" }} title={j.soumis_par_nom || "—"}>{j.soumis_par_nom || "—"}</td>
                    <td className="nowrap">
                      {j.statut === "en_attente" ? (
                        /* pointage.traiter_justifications */
                        peutTraiter ? (
                          <div className="act">
                            <button
                              title="Approuver"
                              disabled={working}
                              onClick={() => handleApprouver(j)}
                              style={{ background: "#28a745", color: "white" }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                            >
                              <i className="ti ti-check" />
                            </button>
                            <button
                              title="Rejeter"
                              disabled={working}
                              onClick={() => setModal({ item: j })}
                              style={{ background: "var(--rd)", color: "white" }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                            >
                              <i className="ti ti-x" />
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, color: "var(--m)" }}>—</span>
                        )
                      ) : (
                        <span style={{ fontSize: 12, color: "var(--m)" }}>{formatDateTime(j.date_traitement)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan="8" style={{ textAlign: "center", color: "var(--h)", padding: 24 }}>
                  Aucune justification trouvée
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {peutTraiter && modal && (
        <RejeterModal
          working={working}
          onClose={() => setModal(null)}
          onConfirm={(commentaire) => handleRejeter(modal.item, commentaire)}
        />
      )}
    </div>
  );
}

function RejeterModal({ working, onClose, onConfirm }) {
  const [commentaire, setCommentaire] = useState("");
  return (
    <Modal title="Rejeter la justification" onClose={onClose}>
      <div style={{ fontSize: 14, color: "var(--m)", marginBottom: 8 }}>
        Précise éventuellement un motif de rejet.
      </div>
      <div className="fg">
        <label>Commentaire (optionnel)</label>
        <textarea
          rows={3}
          placeholder="Commentaire (optionnel)…"
          value={commentaire}
          onChange={e => setCommentaire(e.target.value)}
        />
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={working}>Annuler</button>
        <button className="bprim rd" onClick={() => onConfirm(commentaire)} disabled={working}>
          {working ? "..." : <><i className="ti ti-x" />Rejeter</>}
        </button>
      </div>
    </Modal>
  );
}